import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Botões (manual §4).
 *
 * **Um botão primário âmbar por tela, no máximo.** Se dois elementos disputam o
 * âmbar numa tela, um deles está errado. O âmbar só brilha porque é raro.
 *
 * Área tocável mínima de 44px em contexto móvel — daí a altura `h-11` no `lg` e o
 * `min-h-11` no tamanho padrão em telas pequenas.
 */
export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-semibold text-sm leading-none rounded-md cursor-pointer',
    'transition-colors duration-[var(--dur-fast)] ease-[var(--ease)]',
    'outline-none focus-visible:shadow-[var(--focus-ring)]',
    'disabled:pointer-events-none disabled:opacity-45 disabled:cursor-not-allowed',
    '[&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        // Ação-chave da tela. Âmbar, escasso — e com o halo da marca, que é o que faz o botão
        // parecer aceso em vez de pintado.
        primario: 'bg-primary text-on-primary hover:bg-primary-hover shadow-[var(--halo-primary)]',
        // Apoio: transparente com borda.
        secundario:
          'border border-border bg-surface text-text shadow-[var(--realce-topo)] hover:border-border-strong hover:bg-surface-hover',
        // Terciário e navegações técnicas. Era ciano — cor que não existe no logo; agora é texto
        // neutro que ACENDE em âmbar no hover, o que também torna o alvo mais óbvio.
        fantasma:
          'bg-transparent text-text-secondary hover:bg-surface-hover hover:text-primary',
        // Só para ação destrutiva confirmada.
        perigo: 'bg-danger text-on-danger hover:brightness-110',
      },
      size: {
        default: 'h-10 px-4 py-3',
        sm: 'h-8 px-3 text-[13px]',
        lg: 'h-11 px-5',
        icon: 'size-11',
      },
    },
    defaultVariants: { variant: 'secundario', size: 'default' },
  },
);

// `ComponentProps<'button'>` em vez de `ButtonHTMLAttributes`: no React 19 o `ref`
// é uma prop comum, mas só entra no tipo por esta via. O modal de confirmação
// precisa dele para pôr o foco inicial no "Cancelar".
export interface ButtonProps
  extends React.ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}
